import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import articles from '../../data/articlesData';
import CopyButton from '../partials/CopyButton';

const toAnchorId = (text) =>
  text.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]/g, '');

const copyHeadingLink = (id) => {
  const base = window.location.href.split('#')[0];
  navigator.clipboard.writeText(`${base}#${id}`);
};

const BlockRenderer = ({ block }) => {
  switch (block.type) {
    case 'heading': {
      const id = toAnchorId(block.text);
      const Tag = block.level === 2 ? H2 : H3;
      return (
        <HeadingWrapper>
          <Tag id={id}>{block.text}</Tag>
          <HeadingAnchor
            onClick={() => copyHeadingLink(id)}
            title="Copy link to this section"
          >
            §
          </HeadingAnchor>
        </HeadingWrapper>
      );
    }
    case 'paragraph':
      return <Paragraph>{block.text}</Paragraph>;
    case 'code':
      return (
        <ArticleCodeBlock>
          <ArticleCodeHeader>
            <LangLabel>{block.lang}</LangLabel>
            <CopyButton text={block.code} />
          </ArticleCodeHeader>
          <SyntaxHighlighter
            language={block.lang}
            style={vscDarkPlus}
            customStyle={{
              margin: 0,
              borderRadius: '0 0 6px 6px',
              background: 'var(--bg-surface-2)',
              fontSize: '0.875rem',
              fontFamily: 'var(--font-mono)',
            }}
          >
            {block.code}
          </SyntaxHighlighter>
        </ArticleCodeBlock>
      );
    case 'list': {
      const ListEl = block.ordered ? OL : UL;
      return (
        <ListEl>
          {block.items.map((item, i) => <li key={i}>{item}</li>)}
        </ListEl>
      );
    }
    case 'callout':
      return <Callout>{block.text}</Callout>;
    default:
      return null;
  }
};

const ArticlePage = () => {
  const { slug } = useParams();
  const article = articles.find(a => a.slug === slug);

  useEffect(() => {
    if (article) document.title = `${article.title} — Dmitri Malisev`;
    else document.title = 'Article not found — Dmitri Malisev';
  }, [article]);

  useEffect(() => {
    if (article) {
      const meta = document.querySelector('meta[name="description"]');
      if (meta) {
        meta.setAttribute('content', article.description);
      } else {
        const el = document.createElement('meta');
        el.name = 'description';
        el.content = article.description;
        document.head.appendChild(el);
      }
    }
  }, [article]);

  if (!article) {
    return (
      <Container>
        <BackLink to="/articles">← Articles</BackLink>
        <NotFound>Article not found.</NotFound>
      </Container>
    );
  }

  return (
    <Container>
      <BackLink to="/articles">← Articles</BackLink>

      <ArticleHeader>
        <ArticleMeta>
          <ArticleDate>{article.date}</ArticleDate>
          <ArticleTags>
            {article.tags.map(tag => <ArticleTag key={tag}>{tag}</ArticleTag>)}
          </ArticleTags>
        </ArticleMeta>
        <ArticleTitle>{article.title}</ArticleTitle>
        <ArticleDesc>{article.description}</ArticleDesc>
      </ArticleHeader>

      <ArticleBody>
        {article.blocks.map((block, i) => (
          <BlockRenderer key={i} block={block} />
        ))}
      </ArticleBody>
    </Container>
  );
};

const Container = styled.div`
  padding: 3rem 0 4rem;
  max-width: 740px;
`;

const BackLink = styled(Link)`
  font-family: var(--font-mono);
  font-size: 0.8125rem;
  color: var(--text-muted);
  text-decoration: none;
  display: inline-block;
  margin-bottom: 2.5rem;
  transition: color 0.15s;

  &:hover {
    color: var(--text-primary);
  }
`;

const NotFound = styled.p`
  color: var(--text-muted);
  font-size: 1rem;
`;

const ArticleHeader = styled.div`
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid var(--border);
`;

const ArticleMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const ArticleDate = styled.span`
  font-family: var(--font-mono);
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const ArticleTags = styled.div`
  display: flex;
  gap: 0.375rem;
`;

const ArticleTag = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  padding: 0.15rem 0.45rem;
  border: 1px solid var(--border);
  border-radius: 3px;
  color: var(--text-muted);
`;

const ArticleTitle = styled.h1`
  font-size: 1.875rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 0.75rem;
  letter-spacing: -0.02em;
  line-height: 1.3;

  @media (max-width: 600px) {
    font-size: 1.5rem;
  }
`;

const ArticleDesc = styled.p`
  font-size: 1.0625rem;
  color: var(--text-muted);
  margin: 0;
  line-height: 1.7;
`;

const ArticleBody = styled.div`
  font-size: 1rem;
  line-height: 1.8;
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  margin: 2.5rem 0 1rem;

  &:first-child {
    margin-top: 0;
  }
`;

const H2 = styled.h2`
  font-size: 1.375rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  letter-spacing: -0.01em;
`;

const H3 = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const HeadingAnchor = styled.button`
  background: none;
  border: none;
  color: var(--text-muted);
  font-size: 1rem;
  cursor: pointer;
  padding: 0;
  opacity: 0;
  transition: opacity 0.15s;

  ${HeadingWrapper}:hover & {
    opacity: 1;
  }
`;

const Paragraph = styled.p`
  color: var(--text-muted);
  margin: 0 0 1.25rem;
`;

const ArticleCodeBlock = styled.div`
  margin: 1.5rem 0;
  border: 1px solid var(--border);
  border-radius: 6px;
  overflow: hidden;
`;

const ArticleCodeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;
  background: var(--bg-surface);
  border-bottom: 1px solid var(--border);
`;

const LangLabel = styled.span`
  font-family: var(--font-mono);
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: lowercase;
`;

const UL = styled.ul`
  color: var(--text-muted);
  padding-left: 1.5rem;
  margin: 0 0 1.25rem;

  li {
    margin-bottom: 0.375rem;
  }
`;

const OL = styled.ol`
  color: var(--text-muted);
  padding-left: 1.5rem;
  margin: 0 0 1.25rem;

  li {
    margin-bottom: 0.375rem;
  }
`;

const Callout = styled.div`
  padding: 1rem 1.25rem;
  border-left: 3px solid var(--accent);
  background: var(--accent-subtle);
  color: var(--text-muted);
  font-size: 0.9375rem;
  line-height: 1.7;
  margin: 1.5rem 0;
  border-radius: 0 4px 4px 0;
`;

export default ArticlePage;
